import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { BrowserModule } from '@angular/platform-browser';
import {
    BrowserAnimationsModule,
    NoopAnimationsModule,
} from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { AdminCreditsComponent } from './admin/components/admin-credits/admin-credits.component';
import { AdminNavigationComponent } from './admin/components/admin-navigation/admin-navigation.component';
import { AdminPerformanceComponent } from './admin/components/admin-performance/admin-performance.component';
import { AdminTokenNameFormComponent } from './admin/components/admin-token-name-form/admin-token-name-form.component';
import { AdminTokenComponent } from './admin/components/admin-token/admin-token.component';
import { AdminSettingsComponent } from './admin/components/admin-settings/admin-settings.component';
import { AdminTrainingsComponent } from './admin/components/admin-trainings/admin-trainings.component';
import { AdminUserComponent } from './admin/components/admin-user/admin-user.component';
import { AdminComponent } from './admin/components/admin/admin.component';
import { GlobalConfigComponent } from './admin/components/global-config/global-config.component';
import { UserSettingsComponent } from './admin/components/user-settings/user-settings.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { Configuration } from './authentication/config/configuration';
import { BrowserCompatibilityNoticeComponent } from './authentication/components/browser-compatibility-notice/browser-compatibility-notice.component';
import { ForgetPasswordComponent } from './authentication/reset-password/forget-password/forget-password.component';
import { ResetPasswordComponent } from './authentication/reset-password/reset-password.component';
import { VerifyComponent } from './authentication/verify/verify.component';
import { CardModule } from './card/card.module';
import { provideApplicationHttpClient } from './core/http-client.config';
import { DefaultErrorHandler } from './default-error-handler.service';
import { ImgDbModule } from './img-db/img-db.module';
import { MaterialModule } from './material.module';
import { MessageComponent } from './message/components/message.component';
import { LandingPageComponent } from './navigation/landing-page/landing-page.component';
import { LanguageNavMenuComponent } from './navigation/menu/components/language-nav-menu/language-nav-menu.component';
import { MenuButtonComponent } from './navigation/menu/components/menu-button/menu-button.component';
import { MenuItemComponent } from './navigation/menu/components/menu-item/menu-item.component';
import { MenuComponent } from './navigation/menu/components/menu/menu.component';
import { NavbarComponent } from './navigation/navbar/navbar.component';
import { PageNotFoundComponent } from './navigation/page-not-found/page-not-found.component';
import { SidebarNavComponent } from './navigation/sidebar-nav/components/sidebar-nav/sidebar-nav.component';
import { SliderItemComponent } from './navigation/slider-item/slider-item.component';
import { UserReviewComponent } from './navigation/user-review/user-review.component';
import { PlayerDialogComponent } from './player/components/player-dialog/player-dialog.component';
import { PlayerSelectComponent } from './player/components/player-select/player-select.component';
import { ProjectSplitScreenModule } from './project-split-screen/project-split-screen.module';
import { CommunityContainerComponent } from './project/component/community-project/community-container.component';
import { ContainerSummaryComponent } from './project/component/container-summary/container-summary.component';
import { ContainerWizardComponent } from './project/component/container-wizard/container-wizard.component';
import { UserProjectsComponent } from './project/component/user-projects/user-projects.component';
import { WizardSlideComponent } from './project/component/wizard-slide/wizard-slide.component';
import { AddGitObjectComponent } from './robots/components/add-git-object/add-git-object.component';
import { AddZipObjectComponent } from './robots/components/add-zip-object/add-zip-object.component';
import { UploadTypeDialogComponent } from './robots/components/upload-type-dialog/upload-type-dialog.component';
import { UrdfListerComponent } from './robots/components/urdf-lister/urdf-lister.component';
import { SearchbarComponent } from './searchbar/searchbar.component';
import { SharedModule } from './shared/shared.module';
import { ShopwareCmsModule } from './shopware-cms/shopware-cms.module';
import { SplitScreenModule } from './split-screen/split-screen.module';
import { UserAreaModule } from './user-area/user-area.module';
import { WorkspaceModule } from './workspace/workspace.module';
import { OrganizationAreaModule } from './organization-area/organization-area.module';

@NgModule({
    declarations: [
        AppComponent,
        PageNotFoundComponent,
        NavbarComponent,
        LandingPageComponent,
        AddZipObjectComponent,
        AdminComponent,
        GlobalConfigComponent,
        UserSettingsComponent,
        UrdfListerComponent,
        UploadTypeDialogComponent,
        AddGitObjectComponent,
        MenuComponent,
        SidebarNavComponent,
        MenuItemComponent,
        MenuButtonComponent,
        SliderItemComponent,
        UserReviewComponent,
        CommunityContainerComponent,
        UserReviewComponent,
        SearchbarComponent,
        ResetPasswordComponent,
        ForgetPasswordComponent,
        ForgetPasswordComponent,
        ContainerSummaryComponent,
        UserProjectsComponent,
        MessageComponent,
        MessageComponent,
        AdminNavigationComponent,
        AdminTrainingsComponent,
        AdminPerformanceComponent,
        AdminUserComponent,
        AdminCreditsComponent,
        AdminSettingsComponent,
        PlayerSelectComponent,
        PlayerDialogComponent,
        LanguageNavMenuComponent,
        WizardSlideComponent,
        ContainerWizardComponent,
        VerifyComponent,
        AdminTokenComponent,
        AdminTokenNameFormComponent,
        BrowserCompatibilityNoticeComponent,
    ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        CardModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        NgbModule,
        NoopAnimationsModule,
        MaterialModule,
        SharedModule,
        MatSelectModule,
        MatOptionModule,
        MatDialogModule,
        ScrollingModule,
        BrowserAnimationsModule,
        MatExpansionModule,
        DragDropModule,
        WorkspaceModule,
        SplitScreenModule,
        TranslateModule.forRoot({
            loader: provideTranslateHttpLoader({
                prefix: './assets/i18n/',
                suffix: '.json',
            }),
            fallbackLang: 'en',
        }),
        InfiniteScrollModule,
        ShopwareCmsModule,
        ImgDbModule,
        ProjectSplitScreenModule,
        UserAreaModule,
        OrganizationAreaModule,
    ],
    providers: [
        Configuration,
        NavbarComponent,
        {
            provide: ErrorHandler,
            useClass: DefaultErrorHandler,
        },
        provideApplicationHttpClient(),
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
